import React, { useEffect, useMemo, useState } from "react";
import { useSelector, shallowEqual } from "react-redux";
import { getFunctions, httpsCallable } from "firebase/functions";
import Login from "components/Login";
import { RootState } from "stores/Store";
import championData from "constants/champions/championData.json";
import app from "../firebaseConfig";
import "styles/views/Predictor.scss";

const ROLES = ["Top", "Jungle", "Mid", "Bot", "Support"];

type Bracket = {
  bracket: string;
  lo: number;
  hi: number;
  kind: string;
  threshold: number | null;
  abs_dscore: number;
  passes: boolean;
};

type BracketGuide = {
  side: "blue" | "red" | null;
  brackets: Bracket[];
  summary: string;
};

type BracketLevel = {
  bracket: string;
  lo: number;
  hi: number;
  level: number; // 0 = ne passe pas, 1 = std+, 2 = sharp, 3 = core
};

type ThresholdCell = { threshold: number | null; pct: number | null };
type ThresholdRow = {
  lo: number;
  hi: number;
  kind: string;
  label: string;
  sides: {
    blue: Record<string, ThresholdCell>;
    red: Record<string, ThresholdCell>;
  };
};
type ThresholdsResponse = {
  model_id: number | string | null;
  model_name: string | null;
  sigmoid_scale: number;
  presets: string[];
  brackets: ThresholdRow[];
};

type PredictResponse = {
  dscore: number;
  p_blue: number;
  p_red: number;
  bracket_guide: BracketGuide | null;
  bracket_levels?: BracketLevel[];
  side?: "blue" | "red" | null;
  model_id?: number;
  model_name?: string;
};

const functions = getFunctions(app);
const predictCall = httpsCallable<
  { blue: string[]; red: string[] },
  PredictResponse
>(functions, "predictorEvaluateDraft");
const thresholdsCall = httpsCallable<unknown, ThresholdsResponse>(
  functions,
  "predictorBracketThresholds",
);

function buildChampionNames(): string[] {
  const names: string[] = [];
  Object.values(
    championData as Record<string, { id: string; name: string }>,
  ).forEach((c) => {
    if (c?.id && c.id !== "NoBan" && c.name) names.push(c.name);
  });
  return names.sort((a, b) => a.localeCompare(b));
}

function ChampionInput({
  value,
  onChange,
  label,
  listId,
  inputId,
}: {
  value: string;
  onChange: (v: string) => void;
  label: string;
  listId: string;
  inputId: string;
}) {
  return (
    <div className="predictor__field">
      <label className="predictor__label" htmlFor={inputId}>
        {label}
      </label>
      <input
        id={inputId}
        type="text"
        list={listId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder="Champion"
        autoComplete="off"
        spellCheck={false}
      />
    </div>
  );
}

function formatPct(p: number): string {
  return `${(p * 100).toFixed(1)}%`;
}

function Predictor() {
  const user = useSelector((s: RootState) => s.userReducer.user, shallowEqual);

  const [blue, setBlue] = useState<string[]>(["", "", "", "", ""]);
  const [red, setRed] = useState<string[]>(["", "", "", "", ""]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<PredictResponse | null>(null);
  const [thresholds, setThresholds] = useState<ThresholdsResponse | null>(null);

  const champNames = useMemo(buildChampionNames, []);

  const PREDICTOR_DISABLED = false;

  // Fetch the reference threshold table once the user has access.
  useEffect(() => {
    let cancelled = false;
    if (!PREDICTOR_DISABLED && user?.predictorAccess) {
      thresholdsCall({})
        .then((res) => {
          if (!cancelled) setThresholds(res.data);
        })
        .catch(() => {
          // Silent fail: the threshold panel is informational; predictor
          // remains usable without it.
        });
    }
    return () => {
      cancelled = true;
    };
  }, [user?.predictorAccess]);

  if (PREDICTOR_DISABLED) {
    return (
      <div className="predictor predictor--gate">
        <h2>Predictor désactivé</h2>
        <p>Le predictor est temporairement indisponible.</p>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="predictor predictor--gate">
        <Login message="Connecte-toi pour accéder au predictor." />
      </div>
    );
  }

  if (!user.predictorAccess) {
    return (
      <div className="predictor predictor--gate">
        <h2>Accès restreint</h2>
        <p>
          Cette page est réservée à une liste de comptes autorisés. Si tu
          devrais y avoir accès, contacte Hadrien.
        </p>
      </div>
    );
  }

  const updateSlot = (side: "blue" | "red", idx: number, v: string) => {
    if (side === "blue") {
      const next = [...blue];
      next[idx] = v;
      setBlue(next);
    } else {
      const next = [...red];
      next[idx] = v;
      setRed(next);
    }
  };

  const allFilled = [...blue, ...red].every((c) => c.trim().length > 0);

  const evaluate = async () => {
    setError(null);
    setResult(null);
    setLoading(true);
    try {
      const res = await predictCall({
        blue: blue.map((c) => c.trim()),
        red: red.map((c) => c.trim()),
      });
      setResult(res.data);
    } catch (e) {
      const message =
        e && typeof e === "object" && "message" in e
          ? String((e as { message: unknown }).message)
          : "Erreur inconnue";
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  let winner: "blue" | "red" | null = null;
  if (result && result.dscore > 0) winner = "blue";
  else if (result && result.dscore < 0) winner = "red";

  const levelByBracket: Record<string, number> = {};
  (result?.bracket_levels || []).forEach((bl) => {
    levelByBracket[bl.bracket] = bl.level;
  });

  return (
    <div className="predictor">
      <h1 className="predictor__title">Predictor</h1>
      <p className="predictor__subtitle">
        Saisis les 10 champions, dans l&apos;ordre Top → Support. Le moteur
        DrafterEye renvoie le Dscore, la probabilité de victoire et les brackets
        Std+.
      </p>

      <datalist id="predictor-champ-list">
        {champNames.map((n) => (
          <option key={n} value={n} />
        ))}
      </datalist>

      <div className="predictor__teams">
        <div className="predictor__team predictor__team--blue">
          <h3>Équipe Bleue</h3>
          {ROLES.map((role, i) => (
            <ChampionInput
              key={`b-${role}`}
              inputId={`predictor-blue-${role.toLowerCase()}`}
              label={role}
              listId="predictor-champ-list"
              value={blue[i]}
              onChange={(v) => updateSlot("blue", i, v)}
            />
          ))}
        </div>
        <div className="predictor__team predictor__team--red">
          <h3>Équipe Rouge</h3>
          {ROLES.map((role, i) => (
            <ChampionInput
              key={`r-${role}`}
              inputId={`predictor-red-${role.toLowerCase()}`}
              label={role}
              listId="predictor-champ-list"
              value={red[i]}
              onChange={(v) => updateSlot("red", i, v)}
            />
          ))}
        </div>
      </div>

      <button
        type="button"
        className="predictor__submit"
        onClick={evaluate}
        disabled={!allFilled || loading}
      >
        {loading ? "Calcul en cours…" : "Évaluer le draft"}
      </button>

      {error && <div className="predictor__error">⚠ {error}</div>}

      {result && (
        <div className="predictor__result">
          <div className="predictor__scoreboard">
            <div
              className={`predictor__prob predictor__prob--blue ${
                winner === "blue" ? "is-winner" : ""
              }`}
            >
              <span className="predictor__probLabel">Bleu</span>
              <span className="predictor__probValue">
                {formatPct(result.p_blue)}
              </span>
            </div>
            <div className="predictor__dscore">
              <span className="predictor__dscoreLabel">Dscore</span>
              <span className="predictor__dscoreValue">
                {result.dscore.toFixed(4)}
              </span>
              {result.model_name && (
                <span className="predictor__modelName">
                  {result.model_name}
                </span>
              )}
            </div>
            <div
              className={`predictor__prob predictor__prob--red ${
                winner === "red" ? "is-winner" : ""
              }`}
            >
              <span className="predictor__probLabel">Rouge</span>
              <span className="predictor__probValue">
                {formatPct(result.p_red)}
              </span>
            </div>
          </div>

          {result.bracket_guide && (
            <div className="predictor__brackets">
              <h3>Brackets Std+</h3>
              <p className="predictor__summary">
                {result.bracket_guide.summary}
              </p>
              <div className="predictor__bracketGrid">
                {result.bracket_guide.brackets.map((b) => {
                  const level = levelByBracket[b.bracket] ?? (b.passes ? 1 : 0);
                  return (
                    <div
                      key={b.bracket}
                      className={`predictor__bracketCell ${
                        b.passes ? "passes" : "fails"
                      }`}
                    >
                      <span className="predictor__bracketRange">
                        {b.bracket}
                      </span>
                      <span className="predictor__bracketKind">{b.kind}</span>
                      <span className="predictor__bracketStatus">
                        {b.passes ? `Niveau ${level}` : "✗ insuffisant"}
                      </span>
                      {b.threshold !== null && (
                        <span className="predictor__bracketThresh">
                          seuil {b.threshold.toFixed(3)} · |D|{" "}
                          {b.abs_dscore.toFixed(3)}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {thresholds && (
        <div className="predictor__thresholdsRef">
          <h3>Seuils actuels par bracket de cote</h3>
          <p className="predictor__thresholdsHelp">
            Pour chaque bracket et chaque niveau (1 = std+, 2 = sharp, 3 =
            core), |Dscore| minimum pour déclencher un signal. Le % entre
            parenthèses est la probabilité de victoire implicite (sigmoïde scale{" "}
            {thresholds.sigmoid_scale.toFixed(3)}, modèle{" "}
            {thresholds.model_name || thresholds.model_id}). Recalculé à chaque
            démarrage du moteur (fenêtre glissante de 800 matchs).
          </p>
          {[
            { side: "blue" as const, label: "Pari côté BLUE" },
            { side: "red" as const, label: "Pari côté RED" },
          ].map(({ side, label }) => (
            <div key={side} className="predictor__thresholdsTableBlock">
              <h4
                className={`predictor__thresholdsSideLabel predictor__thresholdsSideLabel--${side}`}
              >
                {label}
              </h4>
              <table className="predictor__thresholdsTable">
                <thead>
                  <tr>
                    <th>Bracket</th>
                    <th>Type</th>
                    <th>Std+ (1)</th>
                    <th>Sharp (2)</th>
                    <th>Core (3)</th>
                  </tr>
                </thead>
                <tbody>
                  {thresholds.brackets.map((row) => (
                    <tr key={row.label}>
                      <td className="mono">{row.label}</td>
                      <td>{row.kind}</td>
                      {["std+", "sharp", "core"].map((preset) => {
                        const cell = row.sides[side][preset];
                        if (!cell || cell.threshold === null) {
                          return (
                            <td key={preset} className="dim">
                              —
                            </td>
                          );
                        }
                        return (
                          <td key={preset}>
                            D ≥ {cell.threshold.toFixed(4)}
                            <span className="dim">
                              {" "}
                              ({cell.pct?.toFixed(1)}%)
                            </span>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default Predictor;
